/**
 * MultiplayerHUDRenderer — multiplayer-specific UI elements:
 * disconnect text, connection status, forfeit hint, game over prompt.
 *
 * Extracted from HUDRenderer as a composition class.
 */

import { Container, Text, TextStyle } from 'pixi.js';
import { WORLD, Z } from '../../constants';
import type { GameState } from '../../types';

export class MultiplayerHUDRenderer {
  private mpDisconnectText: Text;
  private mpConnectionStatus: Text;
  private mpForfeitHint: Text;
  private mpGameOverPrompt: Text;

  constructor(uiContainer: Container) {
    // Game over prompt (below winner text)
    this.mpGameOverPrompt = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 20,
        fill: 0x888888,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.mpGameOverPrompt.anchor.set(0.5, 0.5);
    this.mpGameOverPrompt.position.set(WORLD.CANVAS_WIDTH / 2, WORLD.CANVAS_HEIGHT / 2 + 60);
    this.mpGameOverPrompt.zIndex = Z.HUD_BASE;
    this.mpGameOverPrompt.visible = false;
    uiContainer.addChild(this.mpGameOverPrompt);

    // Disconnect notification
    this.mpDisconnectText = new Text({
      text: 'OPPONENT DISCONNECTED',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 28,
        fontWeight: 'bold',
        fill: 0xff4444,
        align: 'center',
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    this.mpDisconnectText.anchor.set(0.5, 0.5);
    this.mpDisconnectText.position.set(WORLD.CANVAS_WIDTH / 2, WORLD.CANVAS_HEIGHT / 2 + 100);
    this.mpDisconnectText.zIndex = Z.HUD_TICKS;
    this.mpDisconnectText.visible = false;
    uiContainer.addChild(this.mpDisconnectText);

    // Connection status indicator
    this.mpConnectionStatus = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 14,
        fill: 0x00ff88,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.mpConnectionStatus.position.set(10, 10);
    this.mpConnectionStatus.zIndex = Z.HUD_BASE;
    this.mpConnectionStatus.visible = false;
    uiContainer.addChild(this.mpConnectionStatus);

    // Forfeit hint
    this.mpForfeitHint = new Text({
      text: 'F = Forfeit',
      style: new TextStyle({
        fontFamily: '"JetBrains Mono", monospace',
        fontSize: 12,
        fill: 0x555555,
        stroke: { color: 0x000000, width: 2 },
      }),
    });
    this.mpForfeitHint.position.set(10, WORLD.CANVAS_HEIGHT - 25);
    this.mpForfeitHint.zIndex = Z.HUD_BASE;
    this.mpForfeitHint.visible = false;
    uiContainer.addChild(this.mpForfeitHint);
  }

  hide(): void {
    this.mpGameOverPrompt.visible = false;
    this.mpDisconnectText.visible = false;
    this.mpConnectionStatus.visible = false;
    this.mpForfeitHint.visible = false;
  }

  /** Update game over prompt for multiplayer. Returns true if it handled game_over display. */
  updateGameOver(state: GameState): boolean {
    // Post-match renderer now handles all game_over UI inline (status + buttons).
    this.mpGameOverPrompt.visible = false;
    return false;
  }

  /** Update Target Blast game over prompt. */
  updateTargetBlastGameOver(state: GameState): void {
    this.mpGameOverPrompt.visible = true;
    const bestLines = state.modeState.bestTimes.map((t: number, i: number) => `${i + 1}. ${t.toFixed(1)}s`).join('  ');
    const bestText = state.modeState.bestTimes.length > 0 ? `Best: ${bestLines}` : '';
    this.mpGameOverPrompt.text = `${bestText}\n\nSPACE = Replay  |  ESC = Menu`;
    this.mpGameOverPrompt.style.fill = 0x888888;
    this.mpGameOverPrompt.style.fontSize = 18;
    this.mpGameOverPrompt.alpha = 1;
  }

  hideGameOverPrompt(): void {
    this.mpGameOverPrompt.visible = false;
  }

  update(state: GameState): void {
    // Disconnect notification
    if (state.multiplayer?.enabled && !state.multiplayer.connected && state.phase !== 'title_screen' && !state.multiplayer.disconnectPhase) {
      this.mpDisconnectText.visible = true;
      this.mpDisconnectText.alpha = 0.6 + 0.4 * Math.sin(Date.now() * 0.005);
    } else {
      this.mpDisconnectText.visible = false;
    }

    // Connection status + forfeit hint
    if (state.multiplayer?.enabled && state.phase !== 'title_screen' && state.phase !== 'mode_select') {
      this.mpConnectionStatus.visible = true;
      if (state.multiplayer.connected) {
        this.mpConnectionStatus.text = '● ONLINE';
        this.mpConnectionStatus.style.fill = 0x00ff88;
      } else {
        this.mpConnectionStatus.text = '● OFFLINE';
        this.mpConnectionStatus.style.fill = 0xff4444;
      }
      this.mpForfeitHint.visible = false;
    } else {
      this.mpConnectionStatus.visible = false;
      this.mpForfeitHint.visible = false;
    }
  }

  destroy(): void {
    this.mpGameOverPrompt.destroy();
    this.mpDisconnectText.destroy();
    this.mpConnectionStatus.destroy();
    this.mpForfeitHint.destroy();
  }
}
